import { FaxInput } from '@/components/form/fax-input';
import { PhoneInput } from '@/components/form/phone-input';
import Address from '@/components/address';
import { Divider } from '@/components/ui/Divider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { Flex, Input, Spinner } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, FormProvider, Resolver, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { Page } from '../../../components/layout/page';
import { api, PharmacyDto, SavePharmacyDto } from '../../../lib/api';
import { queryClient } from '../../../providers/query-client';
import { phoneSchema, faxOptionalSchema, addressValidationSchema } from '@musa/lib';

const validationSchema: Resolver<SavePharmacyDto> = zodResolver(
    z.object({
        id: z.string().optional(),
        name: z.string().min(1, 'name is required'),
        phone: phoneSchema,
        fax: faxOptionalSchema,
        address: addressValidationSchema,
    }),
);

export function PharmacyForm() {
    const navigate = useNavigate();
    const params = useParams<'pharmacyId'>();

    const rhf = useForm<SavePharmacyDto>({
        defaultValues: {
            id: params.pharmacyId || undefined,
            name: '',
            phone: '',
            fax: null,
            address: {
                address: '',
                city: '',
                state: '',
                zipcode: '',
            },
        },
        resolver: validationSchema,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });
    const {
        control,
        reset,
        handleSubmit,
        register,
        formState: { errors },
    } = rhf;

    // cleanup on unmount
    useEffect(() => {
        return () => queryClient.removeQueries({ queryKey: ['pharmacy', params.pharmacyId] });
    }, [params.pharmacyId]);

    // fetch data
    const { isFetched } = useQuery<PharmacyDto | void>({
        queryKey: ['pharmacy', params.pharmacyId],
        queryFn: async () => {
            try {
                const { data, status } = await api.pharmacy.get(params.pharmacyId as string);

                if (status !== 200 || !data) {
                    navigate('/settings/pharmacies');
                }

                reset(data);

                return data;
            } catch {
                navigate('/settings/pharmacies');
            }
        },
        enabled: Boolean(params.pharmacyId),
    });

    // create data
    const { mutate: mutateCreate, isPending } = useMutation<unknown, unknown, SavePharmacyDto>({
        mutationFn: async (data) => {
            const response = await api.pharmacy.create(data);
            return response.data;
        },
        onSuccess: () => {
            navigate('/settings/pharmacies');
        },
    });

    // update data
    const { mutate: mutateUpdate } = useMutation<unknown, unknown, SavePharmacyDto>({
        mutationFn: async (data) => {
            if (!params.pharmacyId) {
                return;
            }

            const response = await api.pharmacy.edit(params?.pharmacyId, data);

            return response;
        },
        onSuccess: () => {
            navigate('/settings/pharmacies');
        },
    });

    if (params.pharmacyId && !isFetched) {
        return (
            <Spinner
                borderWidth="4px"
                animationDuration="0.65s"
                css={{ '--spinner-track-color': 'purple.100' }}
                color="purple.500"
                size="lg"
            />
        );
    }

    return (
        <Page heading="Pharmacy Details">
            <FormProvider {...rhf}>
                <form
                    noValidate
                    onSubmit={handleSubmit((values) => {
                        const { address, ...rest } = values;

                        if (params.pharmacyId) {
                            mutateUpdate({ ...rest, address: address?.address ? address : undefined });
                        } else {
                            mutateCreate({ ...rest, address: address?.address ? address : undefined });
                        }
                    })}>
                    <Flex gap="8" flexDirection="column">
                        <Card>
                            <FieldGroup>
                                <Field label="Name" required invalid={!!errors.name} errorText={errors.name?.message}>
                                    <Input {...register('name')} />
                                </Field>

                                <Address
                                    isRequired
                                    validAddress={!!errors.address?.address}
                                    errorAddress={errors.address?.address?.message}
                                    validCity={!!errors.address?.city}
                                    errorCity={errors.address?.city?.message}
                                    validState={!!errors.address?.state}
                                    errorState={errors.address?.state?.message}
                                    validZipCode={!!errors.address?.zipcode}
                                    errorZipCode={errors.address?.zipcode?.message}
                                    nameAddress="address.address"
                                    nameCity="address.city"
                                    nameState="address.state"
                                    nameZipCode="address.zipcode"
                                />
                                <Divider colorPalette="gray" />
                                <FieldRow>
                                    <Field label="Phone" required invalid={!!errors.phone} errorText={errors.phone?.message}>
                                        <Controller
                                            name="phone"
                                            control={control}
                                            render={({ field }) => (
                                                <PhoneInput iconSize={16} {...field} onChange={(e) => field.onChange(e.target.value)} />
                                            )}
                                        />
                                    </Field>

                                    <Field label="Fax" invalid={!!errors.fax} errorText={errors.fax?.message}>
                                        <Controller
                                            name="fax"
                                            control={control}
                                            render={({ field }) => (
                                                <FaxInput
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                />
                                            )}
                                        />
                                    </Field>
                                </FieldRow>
                            </FieldGroup>
                        </Card>

                        <Flex alignItems="flex-start" gap="4">
                            <Button type="submit" loading={isPending}>
                                Save
                            </Button>
                            <Link to="/settings/pharmacies">
                                <Button type="button" variant="outline">
                                    Cancel
                                </Button>
                            </Link>
                        </Flex>
                    </Flex>
                </form>
            </FormProvider>
        </Page>
    );
}
